import React, { useState, useEffect } from 'react';
import { upcoming_events } from "../hooks/Apis";
import {
  FaCalendar,
  FaMapMarkerAlt,
  FaUser,
  FaClock,
  FaArrowRight,
  FaExclamationCircle,
  FaChevronRight,
  FaTicketAlt
} from "react-icons/fa";

const EventsSection = ({ limit = 6, showAllLink = false }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestEvent, setLatestEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [otherEvents, setOtherEvents] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${upcoming_events}?limit=${limit}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch events: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        const allEvents = result.data || [];
        setEvents(allEvents);
        
        // Filter only upcoming events (status: 'upcoming')
        const upcomingOnly = allEvents.filter(event => event.status === 'upcoming');
        
        if (upcomingOnly.length > 0) {
          // Latest upcoming event for left side
          setLatestEvent(upcomingOnly[0]);
          // Next upcoming events for right side (max 5)
          setUpcomingEvents(upcomingOnly.slice(1, 6));
        }
        
        // All other events (including non-upcoming) for right side
        setOtherEvents(allEvents.slice(1, 6));
      } else {
        setError(result.error || 'Failed to load events');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Unable to load events. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return '';
    const eventDate = new Date(dateString);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 0) return 'Past';
    return `In ${diffDays} days`;
  };

  if (loading) {
    return (
      <div className="py-10 bg-gray-50 min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-3 text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-10 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center text-red-500 bg-white p-6 rounded-xl shadow-sm border border-red-100">
            <FaExclamationCircle className="mx-auto text-3xl mb-3 opacity-80" />
            <p className="text-sm font-medium">{error}</p>
            <button
              onClick={fetchEvents}
              className="mt-4 px-4 py-1.5 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 text-sm font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            <div className="text-gray-300 text-5xl mb-3">📅</div>
            <h3 className="text-lg font-semibold text-gray-900">No upcoming events</h3>
            <p className="text-sm text-gray-500 mt-1">Check back soon for updates.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Decorative background blobs */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-blue-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 sm:mb-14 gap-4">
          <div>
            <div className="inline-flex items-center gap-3 mb-3">
              <div className="w-10 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full"></div>
              <span className="text-xs sm:text-sm font-bold tracking-widest text-blue-600 uppercase">
                Stay Updated
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight">
              Upcoming <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Events</span>
            </h2>
          </div>
          {showAllLink && (
            <a href="/events" className="hidden md:inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors group">
              View All Events <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
            </a>
          )}
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* LEFT: Featured Event (Takes 7/12 cols) */}
          <div className="lg:col-span-7 flex flex-col">
            {latestEvent ? (
              <div className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl border border-gray-100/50 overflow-hidden transition-all duration-500 hover:-translate-y-2 flex flex-col h-full relative">
                {/* Image Section */}
                <div className="h-56 sm:h-72 overflow-hidden relative">
                  {latestEvent.image_url ? (
                    <img
                      src={latestEvent.image_url}
                      alt={latestEvent.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/800x400?text=Event'; }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                      <FaCalendar className="text-5xl text-blue-200" />
                    </div>
                  )}
                  
                  {/* Floating Overlay Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60"></div>

                  {/* Floating Badges */}
                  <div className="absolute top-4 left-4 z-10">
                    <div className="bg-white/95 backdrop-blur-md rounded-2xl px-4 py-2 shadow-xl border border-white/20 text-center min-w-[70px] transform group-hover:-translate-y-1 transition-transform duration-300">
                      <span className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-0.5">
                        {new Date(latestEvent.date).toLocaleString('en-US', { month: 'short' })}
                      </span>
                      <span className="block text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 leading-none">
                        {new Date(latestEvent.date).getDate()}
                      </span>
                    </div>
                  </div>
                  
                  {latestEvent.is_featured && (
                    <div className="absolute top-4 right-4 z-10">
                      <span className="bg-gradient-to-r from-orange-400 to-yellow-400 text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow-lg uppercase tracking-wider flex items-center gap-1">
                        ★ Featured
                      </span>
                    </div>
                  )}
                </div>

                {/* Content Section */}
                <div className="p-6 sm:p-8 flex flex-col flex-grow bg-white">
                  <div className="flex items-center gap-2 mb-2 text-xs font-medium text-primary">
                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                      {getDaysUntil(latestEvent.date)}
                    </span>
                    {latestEvent.time && (
                       <span className="flex items-center gap-1 text-gray-500">
                         <FaClock className="text-[10px]" /> {formatTime(latestEvent.time)}
                       </span>
                    )}
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                    {latestEvent.title}
                  </h3>

                  {latestEvent.description && (
                    <p className="text-sm sm:text-base text-gray-500 mb-6 line-clamp-2 leading-relaxed">
                      {latestEvent.description}
                    </p>
                  )}

                  <div className="mt-auto pt-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-col gap-2">
                      {latestEvent.location && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                          <FaMapMarkerAlt className="text-blue-500" />
                          <span className="line-clamp-1 max-w-[250px]">{latestEvent.location}</span>
                        </div>
                      )}
                      {latestEvent.speaker_name && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                          <FaUser className="text-orange-500" />
                          <span>{latestEvent.speaker_name}</span>
                        </div>
                      )}
                    </div>

                    {latestEvent.registration_link ? (
                      <a
                        href={latestEvent.registration_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm sm:text-base px-6 py-3 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                      >
                        Register Now <FaArrowRight className="text-xs" />
                      </a>
                    ) : (
                      <span className="inline-flex px-3 py-1.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-lg">
                        Coming Soon
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex flex-col items-center justify-center text-center">
                <FaCalendar className="text-4xl text-gray-200 mb-3" />
                <h3 className="text-base font-semibold text-gray-900">No featured events</h3>
              </div>
            )}
          </div>

            {/* RIGHT: List (Takes 5/12 cols) */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100/50 p-6 sm:p-8 h-full flex flex-col relative overflow-hidden">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span className="w-2 h-6 bg-orange-400 rounded-full"></span>
                  {upcomingEvents.length > 0 ? 'More Events' : 'Recent Events'}
                </h3>
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <FaTicketAlt className="text-blue-500 text-sm" />
                </div>
              </div>

              <div className="flex-grow space-y-4 overflow-y-auto pr-1 custom-scrollbar max-h-[450px]">
                {(upcomingEvents.length > 0 ? upcomingEvents : otherEvents).map((event, index) => {
                  const hasLink = event.registration_link && event.registration_link.trim() !== '';
                  const isUpcoming = event.status === 'upcoming';
                  
                  return (
                    <div key={event.id} className="group/item flex gap-4 items-start p-3 -mx-3 rounded-2xl hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent transition-colors duration-300 cursor-default">
                      {/* Date Box */}
                      {event.date && (
                        <div className="flex-shrink-0 w-16 text-center bg-white rounded-xl py-3 shadow-sm border border-gray-100 group-hover/item:border-blue-200 group-hover/item:shadow-md transition-all duration-300 group-hover/item:-translate-y-1">
                           <span className="block text-[10px] text-gray-500 font-bold uppercase tracking-widest leading-none mb-1">
                             {new Date(event.date).toLocaleString('en-US', { month: 'short' })}
                           </span>
                           <span className="block text-xl font-black text-gray-800 leading-none group-hover/item:text-blue-600 transition-colors">
                             {new Date(event.date).getDate()}
                           </span>
                        </div>
                      )}

                      <div className="flex-grow min-w-0 pt-0.5">
                        <div className="flex justify-between items-start gap-2">
                          <h4 className="text-base font-bold text-gray-900 line-clamp-2 leading-tight group-hover/item:text-blue-600 transition-colors">
                            {event.title}
                          </h4>
                          {!isUpcoming && (
                             <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium capitalize ${
                                event.status === 'ongoing' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                             }`}>
                               {event.status}
                             </span>
                          )}
                        </div>

                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                          {event.time && (
                            <span className="flex items-center gap-1">
                              <FaClock className="text-[10px] text-gray-400" /> {formatTime(event.time)}
                            </span>
                          )}
                          {event.location && (
                            <span className="flex items-center gap-1 max-w-[120px] truncate">
                              <FaMapMarkerAlt className="text-[10px] text-gray-400" /> {event.location}
                            </span>
                          )}
                        </div>

                        {/* Quick Register Link */}
                        {isUpcoming && hasLink && (
                          <div className="mt-2">
                             <a 
                               href={event.registration_link}
                               target="_blank"
                               rel="noopener noreferrer"
                               className="inline-flex items-center text-xs font-semibold text-primary hover:text-primary-dark transition-colors"
                             >
                               Register Now <FaChevronRight className="ml-1 text-[10px]" />
                             </a>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile View All Link */}
              {showAllLink && (
                <div className="mt-4 pt-3 border-t border-gray-50 md:hidden">
                  <a href="/events" className="flex items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-primary w-full py-2 bg-gray-50 rounded-lg">
                    View All Events <FaArrowRight className="text-xs" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsSection;